import React from 'react';
import type { MouseEvent } from 'react';
import { Block } from '@klp/design';
import type { Theme } from '@material-ui/core';
import {
    Divider,
    ListItem,
    ListItemText,
    ListSubheader,
    Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import type { GodkjenningProsessResponse } from '@klp/prosjekt/interfaces';
import { IconName, Illustration } from '@klp/design/Illustrations';
import type { ProsjektSteg } from '@klp/formgenerator/interfaces';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { useStartProsess } from '../ProsjektActions/useStartProcess';
import { getProjectTypeKey } from '@klp/prosjekt/utils';

type MenuPopperAllowedStepsProps = {
    prosjektId: GodkjenningProsessResponse['prosjektId'];
    tillattNesteSteg:
        | GodkjenningProsessResponse['tillattNesteSteg']
        | undefined;
    prosjektskjemaTypeId: GodkjenningProsessResponse['prosjektskjemaType']['id'];
    rekkefolgenummer: GodkjenningProsessResponse['prosjektskjemaType']['rekkefolgenummer'];
};

const styles = (theme: Theme) => ({
    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        paddingRight: theme.spacing(2),
    },
});

export const MenuPopperAllowedSteps = ({
    prosjektId,
    tillattNesteSteg,
    prosjektskjemaTypeId,
    rekkefolgenummer,
}: MenuPopperAllowedStepsProps) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);

    const { startProcess, isLoading } = useStartProsess();

    const handleItemClick =
        (steg: ProsjektSteg) => (event: MouseEvent<HTMLDivElement>) => {
            event.stopPropagation();
            startProcess(
                steg.type,
                prosjektId,
                steg.steg,
                prosjektskjemaTypeId,
                rekkefolgenummer,
            );
        };

    if (!tillattNesteSteg || tillattNesteSteg.length === 0) return null;

    return (
        <>
            <ListSubheader disableSticky={true}>
                <Typography>
                    {t('prosjekt.table.actionMenuItems.stepHeader')}
                </Typography>
            </ListSubheader>
            {tillattNesteSteg.map((steg) => (
                <ListItem
                    key={`${steg.navn}-${steg.type}`}
                    dense={true}
                    button={true}
                    onClick={!isLoading ? handleItemClick(steg) : undefined}
                    disabled={isLoading}
                    role="listitem"
                    tabIndex={isLoading ? -1 : 0}
                    aria-disabled={isLoading}
                    data-testid="MenuPopperAllowedStep"
                >
                    <Block className={classes.iconContainer}>
                        <Illustration icon={IconName.DOC_INTERNAL} />
                    </Block>
                    <ListItemText>
                        <Typography>
                            {t(getProjectTypeKey(steg.type))} -{' '}
                            {t(`prosjekt.table.actionMenuItems.${steg.navn}`)}
                        </Typography>
                    </ListItemText>
                </ListItem>
            ))}
            <Divider />
        </>
    );
};
